export const GoogleAppScopeList = [
  "profile",
  "email",
  "openid",
  "https://www.googleapis.com/auth/analytics.edit",
] as const;
export type GoogleAppScopeType = (typeof GoogleAppScopeList)[number];

export type AccessMode = "offline" | "per-user";
export type AuthMode = {
  accessMode: AccessMode[];
  redirectUrl: string;
};

export const AllStagesIntelligemsAppScopeList = [
  "read_cart_transforms",
  "write_cart_transforms",
  "read_products",
  "write_products",
  "read_inventory",
  "write_inventory",
  "read_checkouts",
  "read_analytics",
  "read_reports",
  "read_themes",
  "write_themes",
  "read_orders",
  "write_orders",
  "read_discounts",
  "write_discounts",
  "write_script_tags",
  "read_shipping",
  "write_shipping",
  "read_customers",
  "write_pixels",
  "read_pixels",
  "read_customer_events",
  "read_content",
  "read_locations",
  "read_checkout_branding_settings",
  "read_files",
  "write_files",
  "unauthenticated_read_product_listings",
  // Storefront `ProductVariant.quantityAvailable` requires it; orgs missing it
  // get `null` quantities. The authoring-time degradation contract is keyed on
  // `organization.scopes`, never inferred from null reads (null also means
  // inventory tracking is off).
  "unauthenticated_read_product_inventory",
  "unauthenticated_read_product_pickup_locations",
  "unauthenticated_read_product_tags",
  "unauthenticated_read_selling_plans",
  "unauthenticated_read_content",
  "unauthenticated_read_metaobjects",
  "unauthenticated_read_customers",
  "unauthenticated_write_customers",
  "unauthenticated_read_customer_tags",
  "unauthenticated_read_checkouts",
  "unauthenticated_write_checkouts",
  "read_checkout_and_accounts_configurations",
] as const;

/**
 * Shopify prefixes every Storefront API scope with this. A storefront access
 * token carries the unauthenticated scopes the app held when the token was
 * minted, so a newly granted one only takes effect on a freshly minted token.
 * Consumers key off the prefix rather than individual scopes so adding a
 * storefront scope to the list above needs no other code change.
 */
export const STOREFRONT_SCOPE_PREFIX = "unauthenticated_";

/**
 * The read half of the storefront scopes. Keyed on the prefix for the same
 * reason as above, so a newly requested `unauthenticated_read_*` scope reaches
 * the browser token without a code change.
 */
export const STOREFRONT_READ_SCOPE_PREFIX = "unauthenticated_read_";

/**
 * The `unauthenticated_write_*` scopes the browser-held storefront token is
 * delegated in addition to the reads.
 *
 * This is an allowlist rather than a prefix because the token ships to every
 * shopper in the plugin config: a storefront write scope reaches the browser
 * only by being named here, so requesting another one later widens the server
 * token alone. Adding an entry hands that capability to anyone who reads the
 * config, so each one needs a browser caller that cannot be served elsewhere.
 *
 * `unauthenticated_write_checkouts` is here for the plugin's cart-metafield
 * write (`plugin/src/init/managers/shopify.ts`), which mirrors the Intelligems
 * cart attributes into `$app` cart metafields so they survive into the order.
 * Shopify gates every cart mutation, `cartMetafieldsSet` included, behind it.
 */
export const BrowserStorefrontWriteScopeList = [] as const;
export type BrowserStorefrontWriteScopeType =
  (typeof BrowserStorefrontWriteScopeList)[number];

export const ProdOnlyIntelligemsAppScopeList = ["read_all_orders"] as const;

export const IntelligemsAppScopeList = [
  ...AllStagesIntelligemsAppScopeList,
  ...ProdOnlyIntelligemsAppScopeList,
] as const;

const IntelligemsPlanList = [
  "2K",
  "5K",
  "10K",
  "15K",
  "20K",
  "35K",
  "50K",
  "75K",
  "100K",
  "150K",
  "200K",
  "300K",
];

export type IntelligemsPlanType = (typeof IntelligemsPlanList)[number];

export type IntelligemsAppScopeType = (typeof IntelligemsAppScopeList)[number];

export const IntelligemsRoleList = [
  "intelligems",
  "member",
  "agency_member",
] as const;
export type IntelligemsRoles = (typeof IntelligemsRoleList)[number];

export const IntelligemsAuthScopesList = [
  "no_auth",
  "shopify_checkout",
  "shared_link",
  "member",
  "agency_member",
  "internal",
  "intelligems",
  "server_to_server", // server to server admin access
  "external_api_key",
  "staff_read", // cross-org read-only staff credential
  "staff_write", // cross-org write staff credential
] as const;

export type IntelligemsAuthScopes = (typeof IntelligemsAuthScopesList)[number];

export const INTELLIGEMS_SCOPE_LEVELS: Record<IntelligemsAuthScopes, number> = {
  no_auth: 0,
  shared_link: 100,
  shopify_checkout: 400,
  external_api_key: 450,
  staff_read: 460,
  staff_write: 470,
  member: 500,
  agency_member: 600,
  intelligems: 1000,
  server_to_server: 1000,
  internal: 1000,
};

export const IntelligemsSlackChannelList = [
  "#dev-app-log",
  "#app-log",
  "#dev-api-messages",
  "#in-the-money",
  "#prod-api-messages",
  "#sales_alerts",
  "#dev-config-deploy",
  "#prod-config-deploy",
  "#christian-dev-channel",
  "#monitoring-alerts-important",
  "#dev-customer-api-alerts",
  "#prod-customer-api-alerts",
  "#dev-billing-log",
  "#prod-billing-log",
  "#dev-billing-error-log",
  "#prod-billing-error-log",
  "#prod-bulk-operations",
  "#esc-tech-shift",
  "#prod-monitoring",
  "#on-off-boarding",
  "#shopify-releases",
  "#ask-intelligems",
  "#ask-intelligems-dev",
  "#ask_billing",
  "#billing-test",
] as const;

export type IntelligemsSlackChannel =
  (typeof IntelligemsSlackChannelList)[number];

export const IntelligemsChannelIdMap: Record<IntelligemsSlackChannel, string> =
  {
    "#dev-app-log": "C049EJ24W12",
    "#app-log": "C03728PV273",
    "#dev-api-messages": "C08FTN6HT2R",
    "#in-the-money": "C06SMG33FCK",
    "#prod-api-messages": "C0376T4SDQC",
    "#sales_alerts": "C03TPC4PYQ3",
    "#dev-config-deploy": "C04V16GE18B",
    "#prod-config-deploy": "C04V3Q296PN",
    "#christian-dev-channel": "C05G53EC5BN",
    "#monitoring-alerts-important": "C05QLM6280G",
    "#dev-customer-api-alerts": "C068WGPTC05",
    "#prod-customer-api-alerts": "C068WQJECCD",
    "#dev-billing-log": "C083JMPL6HJ",
    "#prod-billing-log": "C08ATD0R2SH",
    "#dev-billing-error-log": "C08AFMK42H5",
    "#prod-billing-error-log": "C08AYPC3BT6",
    "#prod-bulk-operations": "C08JXSJQ9EV",
    "#esc-tech-shift": "C03HR6G5TPV",
    "#prod-monitoring": "C06853CV880",
    "#on-off-boarding": "C098NHU67L3",
    "#shopify-releases": "C04835B7KJS",
    "#ask-intelligems": "C0AE723V87L",
    "#ask-intelligems-dev": "C0AEGV1SVLJ",
    "#ask_billing": "C06N34K2SCS",
    "#billing-test": "C0ARGDZM735",
  } as const;

export const IntelligemsWebUiScopeList = [
  "view_analytics",
  "view_shipping_experiment",
  "create_shipping_experiment",
  "start_shipping_experiment",
  "edit_shipping_experiment",
  "view_price_experiment",
  "create_price_experiment",
  "start_price_experiment",
  "edit_price_experiment",
  "view_discount_experiment",
  "create_discount_experiment",
  "start_discount_experiment",
  "edit_discount_experiment",
  "edit_advanced_settings",
  "view_volume_discount",
  "view_new_dashboard",
  "view_content_experiment",
  "create_content_experiment",
  "start_content_experiment",
  "edit_content_experiment",
  "create_combo_price_shipping_test",
  "price_test_subscription_products",
  "price_test_manual_csv_upload",
  "audience_targeting_basic",
  "audience_targeting_advanced",
  "is_high_volume_analytics",
] as const;

export type IntelligemsWebUiScope = (typeof IntelligemsWebUiScopeList)[number];

export type IntelligemsScopes = {
  edit_shipping_experiment?: boolean;
  view_shipping_experiment?: boolean;
  view_volume_discount?: boolean;
  view_new_dashboard?: boolean;
  start_price_experiment?: boolean;
  view_discount_experiment?: boolean;
  audience_targeting_basic?: boolean;
  edit_price_experiment?: boolean;
  view_price_experiment?: boolean;
  edit_discount_experiment?: boolean;
  price_test_manual_csv_upload?: boolean;
  create_combo_price_shipping_test?: boolean;
  view_analytics?: boolean;
  start_shipping_experiment?: boolean;
  price_test_subscription_products?: boolean;
  create_shipping_experiment?: boolean;
  edit_advanced_settings?: boolean;
  audience_targeting_advanced?: boolean;
  start_discount_experiment?: boolean;
  create_price_experiment?: boolean;
  create_discount_experiment?: boolean;
  create_content_experiment?: boolean;
  start_content_experiment?: boolean;
  view_content_experiment?: boolean;
  edit_content_experiment?: boolean;
  test_group_limit?: number;
  concurrent_tests?: number;
  price_test_product_limit?: number;
  shipping_test_flat_rate?: boolean;
  shipping_test_flat_rate_with_threshold?: boolean;
  shipping_test_threshold_only?: boolean;
  shipping_test_tiered_rate?: boolean;
  shipping_test_custom_rate?: boolean;
  shipping_test_rate_limit?: number;
  is_high_volume_analytics?: boolean;
};

export const DemoExperienceIdsList = [
  "05508588-9ee5-46e3-9dcb-ed1d20859f29",
  "167425ab-921f-4c49-a950-e7b5bd316a8c",
  "7b82e56f-049b-4cd3-9e28-cd31ce9f965e",
  "bf493a3e-8fe4-44f6-b180-716dee6fc391",
  "d126e218-7a7b-4262-ab27-02e557522294",
];

export const DemoOrgIdsList = [
  "e8d100f6-a35e-4220-8fa0-b2124ca96c34",
  "ce16eb30-53d4-4d77-9681-a72193609e95",
];

export const StagingDemoOrgId = "bb3b9173-f6b7-4b1f-a035-6d76ff2bfadd";

export const DemoTablesToSkipList = ["user", "organization", "role"];
